import { Radio } from "@jugl-web/ui-components/cross-platform/Radio";
import { cx, useAppVariant } from "@jugl-web/utils";
import { FC, ReactNode } from "react";
import Highlighter from "react-highlight-words";
import { Checkbox } from "../../../Checkbox";
import { InteractiveContainer } from "../../../InteractiveContainer";
import { ReactComponent as CheckIcon } from "../../assets/check.svg";

export interface ListBoxItemComponentProps {
  label: ReactNode;
  isSelected: boolean;
  isInteractive: boolean;
  height: number;
  isDisabled?: boolean;
  startSlot?: ReactNode;
  endSlot?: ReactNode;
  secondaryText?: ReactNode;
  highlightedText?: string;
  metaAttributes?: Record<string, string>;
  className?: string;
  onClick: () => void;
  buttonType?: "checkmark" | "checkbox" | "radio";
}

export const ListBoxItemComponent: FC<ListBoxItemComponentProps> = ({
  label,
  isSelected,
  isInteractive,
  height,
  isDisabled = false,
  startSlot,
  endSlot,
  secondaryText,
  highlightedText,
  metaAttributes,
  className,
  onClick,
  buttonType = "checkmark",
}) => {
  const { isCx } = useAppVariant();

  const renderEndSlot = () => {
    if (endSlot) {
      return endSlot;
    }

    switch (buttonType) {
      case "checkmark":
        return (
          <CheckIcon
            className={cx(
              "shrink-0 transition-opacity",
              isSelected ? "opacity-100" : "opacity-0"
            )}
          />
        );
      case "checkbox":
        return (
          <Checkbox
            readOnly
            size="sm"
            isChecked={isSelected}
            isDisabled={isDisabled}
          />
        );
      case "radio":
        return (
          <Radio readOnly isChecked={isSelected} isDisabled={isDisabled} />
        );
      default:
        return null;
    }
  };

  return (
    <InteractiveContainer
      className={cx(
        "relative flex shrink-0 items-center gap-3 rounded-lg px-3 transition-colors",
        isSelected && (isCx ? "bg-grey-100" : "bg-primary-50"),
        {
          "hover:bg-grey-100": !isSelected && isInteractive,
          "cursor-default": !isInteractive,
          "cursor-not-allowed": isDisabled,
        },
        className
      )}
      style={{ height }}
      onClick={isInteractive && !isDisabled ? onClick : undefined}
      {...metaAttributes}
    >
      {startSlot && <span className="shrink-0 leading-[0]">{startSlot}</span>}
      <div className="flex grow flex-col overflow-hidden">
        <span
          className={cx(
            "truncate font-secondary text-sm leading-[140%]",
            isDisabled && buttonType === "checkmark"
              ? "text-dark-500"
              : "text-dark"
          )}
        >
          {typeof label === "string" && highlightedText ? (
            <Highlighter
              autoEscape
              highlightClassName="text-primary font-medium"
              highlightTag="span"
              textToHighlight={label}
              searchWords={[highlightedText]}
            />
          ) : (
            label
          )}
        </span>
        {secondaryText && (
          <span className="truncate text-xs leading-2 tracking-[0.12px] text-grey-700">
            {secondaryText}
          </span>
        )}
      </div>
      {renderEndSlot()}
    </InteractiveContainer>
  );
};
